import { getStoreDefinition } from '@/composables/StoreDefinition'
import { isDefined } from '@/composables/utils'
import { DatabaseView } from '@/enums/DatabaseView'
import { EndPoint } from '@/enums/EndPoint'
import { SqlComparison } from '@/enums/SqlComparison'
import { SqlOperator } from '@/enums/SqlOperator'
import { SqlOrder } from '@/enums/SqlOrder'
import { TableStoreOptions } from '@/enums/TableStoreOptions'
import type { IAdminsStore } from '@/interfaces/api/IAdminsStore'
import type { IQueryOptions } from '@/interfaces/api/IQueryOptions'
import type { ITableStore } from '@/interfaces/api/ITableStore'
import type { ISimpleOption } from '@/interfaces/ISimpleOption'
import type { AdminRecord } from '@/models/AdminRecord'
import type { User } from '@auth0/auth0-spa-js'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAPIStore } from './ApiStore'

let _store: IAdminsStore

export const useAdminsStore = (): ITableStore => {
  if (_store) {
    return _store
  }

  const option = TableStoreOptions.Admins
  const useStore = defineStore(option.id, getStoreDefinition(option), {
    persist: {
      storage: localStorage,
      pick: ['itemsPerPage']
    }
  })
  _store = useStore() as unknown as IAdminsStore
  _extend(_store)
  return _store
}

const _extend = (store: IAdminsStore) => {
  const { t } = useI18n({ useScope: 'global' })
  const apiStore = useAPIStore()

  // Override the default table with a view.
  store.useView(DatabaseView.AdminWithAppointments)

  store.showEditAppointments = ref<boolean>(false) as unknown as boolean

  store.getAdministratorOptions = async (keyword?: string): Promise<Array<ISimpleOption>> => {
    const query: IQueryOptions = {
      limit: 250,
      isDistinct: true,
      fields: ['id', 'firstName', 'lastName'],
      orderBy: [
        { field: 'lastName', order: SqlOrder.ASC },
        { field: 'firstName', order: SqlOrder.ASC }
      ]
    }
    if (isDefined(keyword) && query.where) {
      query.where[0].operator = SqlOperator.And
      query.where[0].children = [
        {
          field: 'firstName',
          comparison: SqlComparison.Like,
          value1: `${keyword}%`,
          operator: SqlOperator.Or
        },
        {
          field: 'lastName',
          comparison: SqlComparison.Like,
          value1: `${keyword}%`,
          operator: SqlOperator.Or
        },
        {
          field: 'idNumber',
          comparison: SqlComparison.Like,
          value1: `${keyword}%`
        }
      ]
    }
    const response = await apiStore.getMany(EndPoint.Admins, query)
    let options: Array<ISimpleOption> = []
    if (!response.error) {
      const supervisors = response.data?.results || []
      options = supervisors.map((item) => {
        return { title: `${item.lastName}, ${item.firstName}`, value: item.id! }
      })
    }
    return options
  }

  store.getAdminBySubOrEmail = async (user: User): Promise<AdminRecord | null> => {
    if (!user?.email) {
      return null
    }

    let person: AdminRecord | null = null
    const query: IQueryOptions = {
      isDistinct: true,
      where: [
        {
          field: 'sub',
          comparison: SqlComparison.Equal,
          value1: user.sub,
          operator: SqlOperator.Or
        },
        {
          field: 'email',
          comparison: SqlComparison.Equal,
          value1: user.email
        }
      ]
    }

    const response = await apiStore.getMany(EndPoint.Admins, query)
    if (!response.error && response.data.results.length > 0) {
      person = response.data.results[0] as AdminRecord
    }
    return person
  }

  store.loadAdmins = async (): Promise<Array<AdminRecord>> => {
    let admins: Array<AdminRecord> = []
    const query: IQueryOptions = {
      isDistinct: true,
      fields: ['id', 'firstName', 'lastName', 'email'],
      orderBy: [
        { field: 'lastName', order: SqlOrder.ASC },
        { field: 'firstName', order: SqlOrder.ASC }
      ],
      where: [
        {
          field: 'role',
          comparison: SqlComparison.Equal,
          value1: 1
        }
      ]
    }

    const response = await apiStore.getMany(EndPoint.Admins, query)
    if (!response.error && response.data.results.length > 0) {
      admins = response.data.results as Array<AdminRecord>
    }
    return admins
  }

  store.roles = computed(() => {
    return [
      { title: t('roles.programAdmin'), value: 1 },
      { title: t('roles.testAdmin'), value: 2 }
    ]
  }) as unknown as Array<ISimpleOption>

  store.getRoleName = (role: number): string => {
    switch (role) {
      case 1:
        return t('roles.programAdmin')
      case 2:
        return t('roles.testAdmin')
      default:
        return t('roles.unknown')
    }
  }

  store.syncAdminInfo = async (id: number, auth0User: User): Promise<void> => {
    const item = {
      id,
      firstName: auth0User.given_name,
      lastName: auth0User.family_name,
      email: auth0User.email,
      sub: auth0User.sub
    }
    await apiStore.save(EndPoint.Admins, item, true)
  }
}
